import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis, Area, AreaChart } from "recharts";
import { Card, CardContent } from "@/components/ui/card";

const statCards = (reporting) => [
  { label: "Verified Leads", value: reporting.total_verified },
  { label: "Pending Leads", value: reporting.total_pending },
  { label: "Conversion Rate", value: `${reporting.conversion_rate}%` },
  { label: "Verified Today", value: reporting.verified_today },
];

export default function AdminReportingPanel({ reporting, reportingDays, onDaysChange }) {
  return (
    <div className="space-y-6" data-testid="admin-reporting-panel">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-2">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-700" data-testid="admin-reporting-label">
            Reporting Overview
          </p>
          <h2 className="font-display text-3xl font-semibold text-slate-950" data-testid="admin-reporting-title">
            Verified vs pending visibility, conversion, and follow-up momentum
          </h2>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-semibold text-slate-700" htmlFor="reporting-days-select" data-testid="reporting-days-label">
            Reporting window
          </label>
          <select
            id="reporting-days-select"
            value={reportingDays}
            onChange={(event) => onDaysChange(Number(event.target.value))}
            className="h-12 rounded-full border border-[#303B67] bg-[#08133E] px-4 text-sm text-white"
            data-testid="reporting-days-select"
          >
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {statCards(reporting).map((item) => (
          <Card key={item.label} className="rounded-[28px] border-[#3ABDBC]/20 bg-[#08133E]/88 shadow-[0_24px_70px_-45px_rgba(1,11,48,0.75)] backdrop-blur-xl" data-testid={`reporting-stat-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <CardContent className="space-y-2 p-6">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-700">{item.label}</p>
              <p className="font-display text-4xl font-semibold text-slate-950">{item.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid min-w-0 gap-6 xl:grid-cols-[1.25fr_0.75fr]">
        <Card className="min-w-0 rounded-[32px] border-[#3ABDBC]/20 bg-[#08133E]/88 shadow-[0_24px_70px_-45px_rgba(1,11,48,0.75)] backdrop-blur-xl" data-testid="reporting-trend-card">
          <CardContent className="space-y-4 p-6">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-700">Trend</p>
              <p className="mt-2 text-sm leading-7 text-slate-600">Track how many signups are sitting in pending versus fully verified across your selected reporting window.</p>
            </div>
            <div className="h-[280px] min-h-[280px] min-w-0" data-testid="reporting-trend-chart">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} initialDimension={{ width: 640, height: 280 }}>
                <AreaChart data={reporting.trend}>
                  <defs>
                    <linearGradient id="verifiedFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3ABDBC" stopOpacity={0.38} />
                      <stop offset="95%" stopColor="#3ABDBC" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="pendingFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6AE32F" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6AE32F" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#26345F" />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#9BA8C6" }} axisLine={{ stroke: "#303B67" }} tickLine={{ stroke: "#303B67" }} />
                  <YAxis tick={{ fontSize: 12, fill: "#9BA8C6" }} axisLine={{ stroke: "#303B67" }} tickLine={{ stroke: "#303B67" }} />
                  <Tooltip contentStyle={{ background: "#08133E", border: "1px solid #303B67", borderRadius: 10, color: "#FFFFFF" }} labelStyle={{ color: "#FFFFFF" }} />
                  <Legend wrapperStyle={{ color: "#DCE4F4" }} />
                  <Area type="monotone" dataKey="verified_count" name="Verified" stroke="#3ABDBC" fill="url(#verifiedFill)" strokeWidth={2} />
                  <Area type="monotone" dataKey="pending_count" name="Pending" stroke="#6AE32F" fill="url(#pendingFill)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="min-w-0 rounded-[32px] border-[#3ABDBC]/20 bg-[#08133E]/88 shadow-[0_24px_70px_-45px_rgba(1,11,48,0.75)] backdrop-blur-xl" data-testid="reporting-status-card">
          <CardContent className="space-y-4 p-6">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-700">Status breakdown</p>
              <p className="mt-2 text-sm leading-7 text-slate-600">See how leads move from newly verified into contact, shortlist, and pilot readiness.</p>
            </div>
            <div className="h-[280px] min-h-[280px] min-w-0" data-testid="reporting-status-chart">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} initialDimension={{ width: 360, height: 280 }}>
                <BarChart data={reporting.status_breakdown}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#26345F" />
                  <XAxis dataKey="status" tick={{ fontSize: 12, fill: "#9BA8C6" }} axisLine={{ stroke: "#303B67" }} tickLine={{ stroke: "#303B67" }} />
                  <YAxis tick={{ fontSize: 12, fill: "#9BA8C6" }} axisLine={{ stroke: "#303B67" }} tickLine={{ stroke: "#303B67" }} />
                  <Tooltip contentStyle={{ background: "#08133E", border: "1px solid #303B67", borderRadius: 10, color: "#FFFFFF" }} labelStyle={{ color: "#FFFFFF" }} />
                  <Bar dataKey="count" fill="#3ABDBC" radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="min-w-0 rounded-[32px] border-[#3ABDBC]/20 bg-[#08133E]/88 shadow-[0_24px_70px_-45px_rgba(1,11,48,0.75)] backdrop-blur-xl" data-testid="reporting-country-card">
        <CardContent className="space-y-4 p-6">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-700">Country breakdown</p>
            <p className="mt-2 text-sm leading-7 text-slate-600">Compare where verified and pending demand is concentrated across the strongest markets in your waitlist.</p>
          </div>
          <div className="h-[320px] min-h-[320px] min-w-0" data-testid="reporting-country-chart">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} initialDimension={{ width: 900, height: 320 }}>
              <BarChart data={reporting.country_breakdown} layout="vertical" margin={{ left: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#26345F" />
                <XAxis type="number" tick={{ fontSize: 12, fill: "#9BA8C6" }} axisLine={{ stroke: "#303B67" }} tickLine={{ stroke: "#303B67" }} />
                <YAxis type="category" dataKey="country" tick={{ fontSize: 12, fill: "#9BA8C6" }} axisLine={{ stroke: "#303B67" }} tickLine={{ stroke: "#303B67" }} width={110} />
                <Tooltip contentStyle={{ background: "#08133E", border: "1px solid #303B67", borderRadius: 10, color: "#FFFFFF" }} labelStyle={{ color: "#FFFFFF" }} />
                <Legend wrapperStyle={{ color: "#DCE4F4" }} />
                <Bar dataKey="verified_count" name="Verified" fill="#3ABDBC" radius={[0, 10, 10, 0]} />
                <Bar dataKey="pending_count" name="Pending" fill="#6AE32F" radius={[0, 10, 10, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}